import API from "../../../utils/api";

// ── Level 1 ──────────────────────────────────────────────────────────────────

// All category metafields with their source attribute + mapped/total/synced counts.
export const getMetafieldsApi = async () => {
    const response = await API.get('category-metafield-mapping/metafields');
    return { response };
};

// Discover the store's CATEGORY metafields live and add any new ones to the listing.
export const syncMetafieldsApi = async () => {
    const response = await API.post('category-metafield-mapping/sync-metafields', {});
    return { response };
};

// Change which of our attributes feeds a metafield: { metafield_key, source_attribute }.
export const saveMetafieldSourceApi = async (payload) => {
    const response = await API.post('category-metafield-mapping/source', payload);
    return { response };
};

// ── Level 2 (per metafield) ──────────────────────────────────────────────────

// Our values (from the source) with current mapping + the "only in Shopify" list.
// skipNotFoundRedirect: a stray 404 (e.g. during a route transition) must NOT hijack
// the whole app to /error/not-found — see the api.jsx response interceptor.
export const getMetafieldValuesApi = async (metafieldKey) => {
    const response = await API.get(`category-metafield-mapping/${metafieldKey}/values`, { skipNotFoundRedirect: true });
    return { response };
};

// Cached Shopify values for a slot's picker. Pass { slot, search }. slot goes in the PATH
// so each slot is a distinct URL — the axios layer cancels same-URL requests, so two slots
// loading concurrently must not share a URL.
export const getShopifyValuesApi = async (metafieldKey, filters = {}) => {
    const { slot, search } = filters;
    const path = `category-metafield-mapping/${metafieldKey}/shopify-values${slot ? `/${slot}` : ""}`;
    const response = await API.get(path, {
        params: search ? { search } : {},
        skipNotFoundRedirect: true,
    });
    return { response };
};

// Upload an entry image to Google Cloud; returns { url }. Pass a FormData with `image`.
export const uploadEntryImageApi = async (formData) => {
    const response = await API.post('category-metafield-mapping/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { response };
};

// Refresh this metafield's Shopify taxonomy values into the local cache.
export const syncShopifyValuesApi = async (metafieldKey) => {
    const response = await API.post(`category-metafield-mapping/${metafieldKey}/sync`, {});
    return { response };
};

// Save value mappings: { mappings: [{ our_key, our_value_name, shopify_value_gid, shopify_value_name }] }.
export const saveMetafieldMappingsApi = async (metafieldKey, payload) => {
    const response = await API.post(`category-metafield-mapping/${metafieldKey}/mappings`, payload);
    return { response };
};

// Suggest mappings by name (exact/normalized/alias). Not saved.
export const matchByNameApi = async (metafieldKey) => {
    const response = await API.post(`category-metafield-mapping/${metafieldKey}/match-by-name`, {});
    return { response };
};

// Suggest mappings via Claude (confidence + reason). Not saved.
export const aiSuggestMappingsApi = async (metafieldKey) => {
    const response = await API.post(`category-metafield-mapping/${metafieldKey}/ai-suggest`, {});
    return { response };
};

// Part 1 — build the value library in Shopify (create metaobject entries). DRY RUN unless commit.
export const syncToShopifyApi = async (metafieldKey, commit = false) => {
    const response = await API.post(`category-metafield-mapping/${metafieldKey}/sync-to-shopify`, { commit });
    return { response };
};

// ── Mapping Sheet (reuses the existing entry-preview export endpoint) ──────────
// ?download=1 makes the endpoint STREAM the xlsx (blob), like the other export
// endpoints, so the browser downloads it directly (no cross-host stored URL).
export const downloadMappingSheetApi = async () => {
    const response = await API.get('product/shopify-metaobject-entry-preview', {
        params: { download: 1 },
        responseType: 'blob',
    });
    return { response };
};
